import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GenerateLinkDialog } from "./GenerateLinkDialog";
import { ManualEntryDialog } from "./ManualEntryDialog";
import { Input } from "@/components/ui/input";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { PayoutAvatar, StatCard } from "@/components/admin/payouts/primitives";
import { EmptyState } from "@/components/admin/payouts/EmptyState";
import { SubscriptionsSkeleton } from "./Skeletons";
import { fmtDate, planLabel, Pill, STATE_BADGE, Th } from "./subscription-ui";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import { throwIfNotOk } from "@/lib/api-error";
import {
  CaretDown,
  LinkSimple,
  MagnifyingGlass,
  PlusCircle,
  Prohibit,
  ArrowCounterClockwise,
  Users,
} from "@phosphor-icons/react";
import type { TherapistSubscriptionView, SubscriptionState } from "@/types";

type StateFilter = SubscriptionState | "all";

const FILTER_OPTIONS: { value: StateFilter; label: string }[] = [
  { value: "all", label: "Svi" },
  { value: "active", label: "Aktivni" },
  { value: "expired", label: "Istekli" },
  { value: "never_paid", label: "Nije plaćena" },
  { value: "exempt", label: "Oslobođeni" },
];

function typeLabel(t: TherapistSubscriptionView): string {
  return t.payoutMethod === "invoice" ? "Firma (faktura)" : "Fizičko (ugovor)";
}

export function OverviewTable() {
  const qc = useQueryClient();
  const [stateFilter, setStateFilter] = useState<StateFilter>("all");
  const [search, setSearch] = useState("");
  const [genFor, setGenFor] = useState<TherapistSubscriptionView | null>(null);
  const [manualFor, setManualFor] = useState<TherapistSubscriptionView | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "subscription-overview"],
    queryFn: async () => {
      const res = await fetch("/api/admin/subscription-overview");
      await throwIfNotOk(res, "Greška pri učitavanju podataka o pretplatama");
      return res.json() as Promise<TherapistSubscriptionView[]>;
    },
  });

  const exemptMutation = useMutation({
    mutationFn: async ({ id, exempt }: { id: string; exempt: boolean }) => {
      const res = await fetch(`/api/admin/therapists/${id}/subscription-exempt`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exempt }),
      });
      await throwIfNotOk(res, "Greška pri izmjeni oslobođenja");
      return exempt;
    },
    onSuccess: (exempt) => {
      toast.success(exempt ? "Terapeut oslobođen pretplate" : "Oslobođenje uklonjeno");
      qc.invalidateQueries({ queryKey: ["admin", "subscription-overview"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const counts = useMemo(() => {
    const c = { active: 0, expired: 0, never_paid: 0, exempt: 0 };
    for (const t of rows) c[t.state] += 1;
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((t) => stateFilter === "all" || t.state === stateFilter)
      .filter(
        (t) =>
          !q ||
          t.name.toLowerCase().includes(q) ||
          t.email.toLowerCase().includes(q),
      );
  }, [rows, stateFilter, search]);

  const hasFilters = stateFilter !== "all" || search.trim() !== "";
  const clearFilters = () => {
    setStateFilter("all");
    setSearch("");
  };

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Aktivne pretplate" value={String(counts.active)} tone="primary" />
        <StatCard label="Istekle" value={String(counts.expired)} />
        <StatCard label="Nije plaćena" value={String(counts.never_paid)} />
        <StatCard label="Oslobođeni" value={String(counts.exempt)} />
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-border/60 bg-card px-4 py-4 shadow-sm sm:px-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <SegmentedFilter
            ariaLabel="Filter po statusu pretplate"
            options={FILTER_OPTIONS}
            value={stateFilter}
            onChange={setStateFilter}
          />
          <div className="relative flex-1 lg:min-w-[200px]">
            <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pretraga po imenu ili emailu…"
              className="bg-card pl-9"
            />
          </div>
          <span className="whitespace-nowrap text-xs text-muted-foreground lg:pl-1">
            {filtered.length} terapeuta
          </span>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <SubscriptionsSkeleton />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Nema terapeuta"
          description={
            hasFilters
              ? "Pokušajte sa drugim filterima."
              : "Još nema terapeuta sa pretplatom."
          }
          hasFilters={hasFilters}
          onClear={clearFilters}
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm lg:block">
            <table className="w-full table-fixed text-[13px]">
              <colgroup>
                <col />
                <col className="w-[150px]" />
                <col className="w-[120px]" />
                <col className="w-[110px]" />
                <col className="w-[136px]" />
                <col className="w-[104px]" />
              </colgroup>
              <thead>
                <tr className="bg-muted/40">
                  <Th className="pl-4">Terapeut</Th>
                  <Th>Tip</Th>
                  <Th>Plan</Th>
                  <Th>Ističe</Th>
                  <Th>Status</Th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((t, i) => (
                  <tr
                    key={t.therapistId}
                    className={cn(
                      "transition-colors hover:bg-primary/[0.03]",
                      i !== filtered.length - 1 && "border-b border-border/40",
                    )}
                  >
                    <td className="px-3 py-3 pl-4">
                      <div className="flex items-center gap-2.5">
                        <PayoutAvatar
                          name={t.name}
                          tone="primary"
                          className="size-7 text-[10px]"
                        />
                        <div className="min-w-0">
                          <div className="truncate font-medium leading-tight text-foreground">
                            {t.name}
                          </div>
                          <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                            {t.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">
                      <div className="truncate">{typeLabel(t)}</div>
                      {t.isVolunteer && (
                        <div className="mt-0.5 text-[11px] text-muted-foreground/70">
                          volonter
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-foreground">
                      {planLabel(t.latestPlan)}
                    </td>
                    <td
                      className={cn(
                        "whitespace-nowrap px-3 py-3 tabular-nums",
                        t.state === "expired"
                          ? "font-medium text-rose-600"
                          : "text-foreground",
                      )}
                    >
                      {fmtDate(t.latestExpiresAt)}
                    </td>
                    <td className="px-3 py-3">
                      <Pill
                        label={STATE_BADGE[t.state].label}
                        tone={STATE_BADGE[t.state].tone}
                      />
                    </td>
                    <td className="py-3 pl-1 pr-4 text-right">
                      <RowActions
                        therapist={t}
                        onGenerateLink={() => setGenFor(t)}
                        onManualEntry={() => setManualFor(t)}
                        onToggleExempt={() =>
                          exemptMutation.mutate({
                            id: t.therapistId,
                            exempt: !t.subscriptionExempt,
                          })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile/Tablet cards */}
          <div className="grid gap-3 sm:grid-cols-2 lg:hidden">
            {filtered.map((t) => (
              <div
                key={t.therapistId}
                className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <PayoutAvatar
                      name={t.name}
                      tone="primary"
                      className="size-8 text-[10px]"
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium leading-tight text-foreground">
                        {t.name}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-muted-foreground">
                        {t.email}
                      </div>
                    </div>
                  </div>
                  <Pill
                    label={STATE_BADGE[t.state].label}
                    tone={STATE_BADGE[t.state].tone}
                  />
                </div>

                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-border/40 pt-3 text-xs">
                  <MobileLine label="Tip">
                    {typeLabel(t)}
                    {t.isVolunteer && (
                      <span className="ml-1 text-muted-foreground/70">
                        · volonter
                      </span>
                    )}
                  </MobileLine>
                  <MobileLine label="Plan">{planLabel(t.latestPlan)}</MobileLine>
                  <MobileLine label="Ističe">
                    <span
                      className={cn(
                        "tabular-nums",
                        t.state === "expired" && "font-medium text-rose-600",
                      )}
                    >
                      {fmtDate(t.latestExpiresAt)}
                    </span>
                  </MobileLine>
                  <div className="flex items-end justify-end">
                    <RowActions
                      therapist={t}
                      onGenerateLink={() => setGenFor(t)}
                      onManualEntry={() => setManualFor(t)}
                      onToggleExempt={() =>
                        exemptMutation.mutate({
                          id: t.therapistId,
                          exempt: !t.subscriptionExempt,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {genFor && <GenerateLinkDialog therapist={genFor} onClose={() => setGenFor(null)} />}
      {manualFor && <ManualEntryDialog therapist={manualFor} onClose={() => setManualFor(null)} />}
    </div>
  );
}

function MobileLine({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 truncate text-foreground">{children}</div>
    </div>
  );
}

function RowActions({
  therapist,
  onGenerateLink,
  onManualEntry,
  onToggleExempt,
}: {
  therapist: TherapistSubscriptionView;
  onGenerateLink: () => void;
  onManualEntry: () => void;
  onToggleExempt: () => void;
}) {
  const [open, setOpen] = useState(false);

  const run = (action: () => void) => () => {
    setOpen(false);
    action();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "group inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border bg-card pl-1.5 pr-3 text-[11px] font-semibold transition-all duration-200 active:scale-[0.96]",
            open
              ? "border-primary bg-primary text-primary-foreground shadow-md shadow-primary/25"
              : "border-primary/25 text-primary hover:border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-md hover:shadow-primary/25",
          )}
        >
          <span
            className={cn(
              "flex size-[18px] items-center justify-center rounded-full transition-all duration-200 group-hover:bg-white/20",
              open ? "bg-white/20" : "bg-primary/10",
            )}
          >
            <CaretDown
              weight="bold"
              className={cn(
                "size-3 transition-transform duration-200",
                open && "rotate-180",
              )}
            />
          </span>
          Akcije
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 p-1.5">
        <div className="flex flex-col">
          {therapist.state !== "exempt" && (
            <ActionItem
              icon={<LinkSimple className="size-4" />}
              onClick={run(onGenerateLink)}
            >
              Generiši payment link
            </ActionItem>
          )}
          <ActionItem
            icon={<PlusCircle className="size-4" />}
            onClick={run(onManualEntry)}
          >
            Dodaj pretplatu ručno
          </ActionItem>
          <ActionItem
            icon={
              therapist.subscriptionExempt ? (
                <ArrowCounterClockwise className="size-4" />
              ) : (
                <Prohibit className="size-4" />
              )
            }
            onClick={run(onToggleExempt)}
          >
            {therapist.subscriptionExempt
              ? "Ukloni oslobođenje"
              : "Označi oslobođenim"}
          </ActionItem>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ActionItem({
  icon,
  onClick,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted"
    >
      <span className="text-muted-foreground">{icon}</span>
      {children}
    </button>
  );
}
